import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Sparkles,
  Building2,
  GitBranch,
  FileText,
  Users,
  MessageSquare,
  BarChart3,
  Gem,
  Brain,
  ShieldCheck,
  Network,
  Headphones,
  Crown,
  Zap,
  Mail,
  Minus,
  Check,
  TrendingUp,
  Clock,
  Activity,
} from "lucide-react";
import { TopNav, Footer, Reveal } from "./LandingPage";

/* ─── Header ─── */
const PricingHeader = ({ billing, setBilling }) => (
  <section className="relative pt-36 pb-12 overflow-hidden" data-testid="pricing-header">
    <div className="absolute inset-0 pointer-events-none">
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 20% 0%, rgba(99, 102, 241,0.16) 0%, transparent 45%), radial-gradient(circle at 80% 30%, rgba(99,102,241,0.10) 0%, transparent 50%)",
        }}
      />
      <div className="absolute inset-0 bg-[linear-gradient(rgba(15,23,42,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(15,23,42,0.04)_1px,transparent_1px)] bg-[size:48px_48px] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_70%)]" />
    </div>
    <div className="relative max-w-[1200px] mx-auto px-6 text-center">
      <Reveal>
        <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-indigo-200 bg-indigo-50 text-[12px] font-semibold text-indigo-700">
          <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 cf-pulse" />
          Pricing · Built for modern estate agencies
        </span>
      </Reveal>
      <Reveal delay={80}>
        <h1 className="font-display text-[52px] sm:text-[68px] lg:text-[76px] font-bold tracking-[-0.035em] leading-[0.98] text-slate-900 max-w-[920px] mx-auto mt-7">
          Pricing built for modern
          <br />
          <span
            className="cf-grad-text"
            style={{ backgroundImage: "linear-gradient(135deg, #818CF8 0%, #4F6BFF 50%, #6366F1 100%)" }}
          >
            estate agencies.
          </span>
        </h1>
      </Reveal>
      <Reveal delay={160}>
        <p className="text-[16px] sm:text-[18px] text-slate-600 mt-7 max-w-[680px] mx-auto leading-relaxed">
          Reduce delays, improve visibility and keep every transaction connected. One operating system. Per branch. No surprises.
        </p>
      </Reveal>
      <Reveal delay={240}>
        <BillingToggle billing={billing} setBilling={setBilling} />
      </Reveal>
    </div>
  </section>
);

/* ─── Billing toggle ─── */
const BillingToggle = ({ billing, setBilling }) => (
  <div className="mt-9 inline-flex items-center gap-1 rounded-2xl border border-slate-200 bg-white p-1 shadow-[0_2px_6px_rgba(15,23,42,0.04)]" data-testid="billing-toggle">
    {[
      { id: "monthly", label: "Monthly" },
      { id: "yearly", label: "Yearly", note: "Save 20%" },
    ].map((b) => (
      <button
        key={b.id}
        data-testid={`billing-${b.id}`}
        onClick={() => setBilling(b.id)}
        className={`relative h-10 px-5 rounded-xl text-[13px] font-semibold transition-all ${
          billing === b.id
            ? "text-white"
            : "text-slate-600 hover:text-slate-900"
        }`}
        style={billing === b.id ? {
          background: "linear-gradient(180deg, #6366F1 0%, #4F6BFF 100%)",
          boxShadow: "0 8px 22px -6px rgba(99, 102, 241,0.45), inset 0 1px 0 rgba(255,255,255,0.20)",
        } : undefined}
      >
        {b.label}
        {b.note && (
          <span
            className={`ml-2 inline-flex items-center text-[10px] font-bold uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-md transition-colors ${
              billing === b.id ? "bg-white/20 text-white" : "bg-indigo-50 text-indigo-700"
            }`}
          >
            {b.note}
          </span>
        )}
      </button>
    ))}
  </div>
);

/* ─── Pricing tiers ─── */
const tierData = [
  {
    id: "core",
    name: "Core",
    target: "For small independent agencies",
    monthly: 249,
    yearly: 199,
    features: [
      { icon: Building2, label: "Deal Rooms" },
      { icon: GitBranch, label: "Sales Progression" },
      { icon: FileText, label: "Document Hub (basic)" },
      { icon: Users, label: "Client Portal" },
      { icon: MessageSquare, label: "Transaction messaging" },
      { icon: BarChart3, label: "Basic analytics" },
    ],
    cta: { label: "Start free trial", href: "#contact" },
    icon: Zap,
  },
  {
    id: "pro",
    name: "Pro",
    target: "For growing & multi-branch agencies",
    monthly: 599,
    yearly: 479,
    popular: true,
    features: [
      { icon: CheckCircle2, label: "Everything in Core, plus:" },
      { icon: Sparkles, label: "AI Risk Intelligence" },
      { icon: Gem, label: "Offer Intelligence" },
      { icon: BarChart3, label: "Director Dashboard" },
      { icon: TrendingUp, label: "Advanced analytics" },
      { icon: Brain, label: "AI summaries on every deal" },
      { icon: Users, label: "Team performance tracking" },
      { icon: Activity, label: "Workflow automation" },
      { icon: Headphones, label: "Priority support" },
    ],
    cta: { label: "Book demo", href: "#contact" },
    icon: Sparkles,
  },
  {
    id: "enterprise",
    name: "Enterprise",
    target: "For multi-office groups & franchises",
    custom: true,
    features: [
      { icon: Network, label: "Multi-branch analytics" },
      { icon: ShieldCheck, label: "Advanced permissions & roles" },
      { icon: Crown, label: "Dedicated onboarding" },
      { icon: Zap, label: "API integrations" },
      { icon: BarChart3, label: "Enterprise reporting" },
      { icon: Activity, label: "Custom workflows" },
      { icon: Headphones, label: "Premium 24/5 support" },
      { icon: Crown, label: "Strategic account management" },
    ],
    cta: { label: "Contact sales", href: "mailto:hello@chainflow.io" },
    icon: Crown,
  },
];

const PricingTiers = ({ billing }) => {
  return (
    <section className="relative pb-24" data-testid="pricing-tiers">
      <div className="max-w-[1280px] mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 lg:gap-6 items-stretch">
          {tierData.map((t, i) => (
            <Reveal key={t.id} delay={i * 100}>
              <TierCard tier={t} billing={billing} />
            </Reveal>
          ))}
        </div>
        <Reveal delay={400}>
          <p className="text-center text-[12.5px] text-slate-500 mt-10 inline-flex items-center justify-center gap-4 w-full">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck size={12} /> SOC 2 ready</span>
            <span className="text-slate-300">·</span>
            <span>14-day free trial</span>
            <span className="text-slate-300">·</span>
            <span>Cancel anytime</span>
            <span className="text-slate-300">·</span>
            <span>Per-branch pricing</span>
          </p>
        </Reveal>
      </div>
    </section>
  );
};

const TierCard = ({ tier, billing }) => {
  const popular = tier.popular;
  const price = tier.custom ? null : billing === "yearly" ? tier.yearly : tier.monthly;
  const Icon = tier.icon;
  return (
    <div
      data-testid={`tier-${tier.id}`}
      className={`relative h-full rounded-3xl p-8 border transition-all ${
        popular ? "border-indigo-300" : "border-slate-200 bg-white hover:border-slate-300"
      }`}
      style={{
        background: popular
          ? "linear-gradient(160deg, #FFFFFF 0%, #F5F3FF 100%)"
          : undefined,
        boxShadow: popular
          ? "0 60px 140px -40px rgba(99, 102, 241,0.40), 0 30px 80px -30px rgba(99, 102, 241,0.18), inset 0 1px 0 rgba(255,255,255,1)"
          : "0 1px 2px rgba(15,23,42,0.04), 0 12px 28px -12px rgba(15,23,42,0.06)",
      }}
    >
      {popular && (
        <>
          <span
            className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.22em] text-white"
            style={{
              background: "linear-gradient(180deg, #818CF8 0%, #4F6BFF 100%)",
              boxShadow: "0 8px 22px -4px rgba(99, 102, 241,0.55), inset 0 1px 0 rgba(255,255,255,0.4)",
            }}
            data-testid="popular-badge"
          >
            <Sparkles size={11} strokeWidth={2.4} className="text-white" /> Most popular
          </span>
          <div className="absolute -top-32 -right-32 w-72 h-72 cf-blob opacity-30 pointer-events-none" />
        </>
      )}

      <div className="relative">
        <div className="flex items-start justify-between">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
            popular ? "bg-indigo-100 border border-indigo-200" : "bg-slate-50 border border-slate-200"
          }`}>
            <Icon size={18} className={popular ? "text-indigo-700" : "text-slate-700"} strokeWidth={1.6} />
          </div>
        </div>
        <p className="font-display text-slate-900 text-[24px] font-bold mt-6 tracking-[-0.022em]">{tier.name}</p>
        <p className="text-[12.5px] text-slate-500 mt-1.5 leading-relaxed">{tier.target}</p>

        <div className="mt-7 pb-6 border-b border-slate-200">
          {tier.custom ? (
            <div>
              <p className="font-display text-slate-900 text-[44px] sm:text-[52px] font-bold leading-none tracking-[-0.035em]">Custom</p>
              <p className="text-[12.5px] text-slate-500 mt-2.5">Tailored to your branch network · annual contract</p>
            </div>
          ) : (
            <div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-[22px] text-slate-400 cf-num">£</span>
                <p className="font-display text-slate-900 text-[56px] font-bold leading-none cf-num tracking-[-0.038em]">{price}</p>
                <span className="text-[14px] text-slate-500 cf-num ml-0.5">/mo</span>
              </div>
              <p className="text-[12px] text-slate-500 mt-2.5">
                Per branch · billed {billing === "yearly" ? "yearly (save 20%)" : "monthly"}
                {billing === "yearly" && (
                  <span className="text-indigo-700 font-semibold"> · £{tier.monthly}/mo monthly</span>
                )}
              </p>
            </div>
          )}
        </div>

        <a
          href={tier.cta.href}
          data-testid={`tier-${tier.id}-cta`}
          className={`mt-6 w-full inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl text-[14px] font-semibold transition-all ${
            popular
              ? "text-white"
              : "text-slate-900 border border-slate-200 hover:border-slate-300 bg-white hover:bg-slate-50"
          }`}
          style={popular ? {
            background: "linear-gradient(180deg, #6366F1 0%, #4F6BFF 100%)",
            boxShadow: "0 14px 36px -10px rgba(99, 102, 241, 0.55), inset 0 1px 0 rgba(255,255,255,0.22)",
          } : undefined}
          onMouseEnter={(e) => popular && (e.currentTarget.style.transform = "translateY(-1px)")}
          onMouseLeave={(e) => popular && (e.currentTarget.style.transform = "translateY(0)")}
        >
          {tier.cta.label} <ArrowRight size={14} />
        </a>

        <ul className="mt-7 space-y-3.5">
          {tier.features.map((f, i) => (
            <li key={i} className="flex items-start gap-2.5 text-[13px]">
              <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-indigo-500" />
              <span className={`${i === 0 && tier.id === "pro" ? "font-semibold text-indigo-700" : "text-slate-700"}`}>
                {f.label}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

/* ─── Value positioning ─── */
const ValuePositioning = () => (
  <section className="relative py-24 bg-slate-50/60" data-testid="pricing-value">
    <div className="max-w-[1100px] mx-auto px-6">
      <Reveal>
        <div
          className="relative rounded-3xl border border-indigo-200 p-12 sm:p-14 overflow-hidden bg-gradient-to-br from-white via-indigo-50/40 to-white"
          style={{ boxShadow: "0 40px 100px -30px rgba(99, 102, 241,0.30), 0 1px 2px rgba(15,23,42,0.04)" }}
        >
          <div className="absolute -top-32 -left-32 w-80 h-80 cf-blob opacity-30" />
          <div className="relative grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border border-indigo-200 text-indigo-700 text-[10.5px] font-semibold uppercase tracking-[0.22em]">
                Why it pays
              </span>
              <h2 className="font-display text-[36px] sm:text-[44px] font-bold tracking-[-0.03em] text-slate-900 leading-[1.05] mt-6">
                One saved transaction
                <br />
                <span className="cf-grad-text" style={{ backgroundImage: "linear-gradient(135deg,#818CF8,#4F6BFF)" }}>
                  pays for ChainFlow many times over.
                </span>
              </h2>
              <p className="text-[15.5px] text-slate-600 mt-6 leading-relaxed max-w-[560px]">
                The average UK chain collapse costs an agency <span className="text-slate-900 font-semibold">£11,400</span> in lost commission,
                wasted progression hours and remarketing. Prevent just one per branch per quarter and the platform pays for itself
                a dozen times over.
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-2xl border border-slate-200 bg-white p-6" style={{ boxShadow: "0 12px 28px -12px rgba(15,23,42,0.10)" }}>
                <p className="text-[10px] uppercase tracking-[0.22em] text-indigo-700 font-bold">Indicative monthly ROI</p>
                <p className="font-display text-slate-900 text-[44px] font-bold mt-3 cf-num tracking-[-0.035em] leading-none">
                  £14,200<span className="text-[14px] text-slate-500 font-normal cf-num">/mo</span>
                </p>
                <p className="text-[12px] text-slate-500 mt-2 leading-relaxed">
                  Based on a 4-branch agency on the Pro plan. Composed of saved fall-throughs, faster exchanges and reduced progression hours.
                </p>
                <hr className="cf-hairline my-5" />
                {[
                  { l: "Saved fall-throughs", v: "£8,640" },
                  { l: "Faster exchanges (+commission timing)", v: "£3,200" },
                  { l: "Progression hour savings", v: "£2,360" },
                ].map((r) => (
                  <div key={r.l} className="flex items-center justify-between py-1.5 text-[12.5px]">
                    <span className="text-slate-500">{r.l}</span>
                    <span className="cf-num text-slate-900 font-semibold">{r.v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ─── ROI metrics ─── */
const ROISection = () => {
  const metrics = [
    { v: "−42%", l: "Time saved per deal", s: "Avg time-to-exchange reduction" },
    { v: "−56%", l: "Fall-through reduction", s: "Versus UK industry baseline" },
    { v: "+38%", l: "Progression efficiency", s: "More transactions per progressor" },
    { v: "+3.1×", l: "Productivity gains", s: "Active deals managed per agent" },
    { v: "100%", l: "Pipeline visibility", s: "Real-time across every branch" },
  ];
  return (
    <section className="relative py-24" data-testid="pricing-roi">
      <div className="max-w-[1280px] mx-auto px-6">
        <Reveal>
          <div className="text-center max-w-[760px] mx-auto">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 text-[10.5px] font-semibold uppercase tracking-[0.22em]">
              ROI
            </span>
            <h2 className="font-display text-[36px] sm:text-[48px] font-bold tracking-[-0.03em] text-slate-900 leading-[1.05] mt-6">
              The numbers our customers see.
            </h2>
            <p className="text-[15.5px] text-slate-600 mt-6 leading-relaxed">
              Aggregated impact across pilot agencies in their first six months on the platform.
            </p>
          </div>
        </Reveal>
        <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {metrics.map((m, i) => (
            <Reveal key={m.l} delay={i * 80}>
              <div className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-white p-6 h-full">
                <p className="font-display text-indigo-700 text-[40px] font-bold cf-num tracking-[-0.035em] leading-none">{m.v}</p>
                <p className="text-slate-900 text-[13.5px] font-semibold mt-4">{m.l}</p>
                <p className="text-[11.5px] text-slate-500 mt-1.5 leading-relaxed">{m.s}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Comparison table ─── */
const sections = [
  {
    title: "Workspace",
    rows: [
      { l: "Deal Rooms", core: true, pro: true, ent: true },
      { l: "Sales Progression", core: true, pro: true, ent: true },
      { l: "Client Portal", core: true, pro: true, ent: true },
      { l: "Transaction messaging", core: true, pro: true, ent: true },
      { l: "Document Hub", core: "Basic", pro: "Advanced", ent: "Enterprise + audit" },
      { l: "Active branches", core: "Up to 2", pro: "Up to 8", ent: "Unlimited" },
      { l: "Active users", core: "Up to 15", pro: "Up to 80", ent: "Unlimited" },
    ],
  },
  {
    title: "Intelligence",
    rows: [
      { l: "Basic analytics", core: true, pro: true, ent: true },
      { l: "AI Risk Intelligence", core: false, pro: true, ent: true },
      { l: "Offer Intelligence", core: false, pro: true, ent: true },
      { l: "AI deal summaries", core: false, pro: true, ent: true },
      { l: "Director Dashboard", core: false, pro: true, ent: true },
      { l: "Multi-branch analytics", core: false, pro: false, ent: true },
      { l: "Team performance tracking", core: false, pro: true, ent: true },
      { l: "Forecasting & modelling", core: false, pro: "Standard", ent: "Custom" },
    ],
  },
  {
    title: "Automation & integrations",
    rows: [
      { l: "Workflow automation", core: false, pro: true, ent: true },
      { l: "API access", core: false, pro: "Read", ent: "Read + write" },
      { l: "CRM/MA integrations", core: false, pro: "Standard", ent: "Custom" },
      { l: "Custom workflows", core: false, pro: false, ent: true },
      { l: "Webhooks", core: false, pro: true, ent: true },
    ],
  },
  {
    title: "Security & support",
    rows: [
      { l: "SSO (Google / Microsoft)", core: false, pro: true, ent: true },
      { l: "SAML & SCIM", core: false, pro: false, ent: true },
      { l: "Advanced permissions & roles", core: false, pro: "Standard", ent: "Granular" },
      { l: "Audit log", core: "30d", pro: "1y", ent: "Unlimited" },
      { l: "Dedicated onboarding", core: false, pro: false, ent: true },
      { l: "Support", core: "Standard", pro: "Priority", ent: "24/5 + named CSM" },
      { l: "SLA", core: false, pro: "99.9%", ent: "99.95% + custom" },
    ],
  },
];

const Cell = ({ value }) => {
  if (value === true) return <Check size={15} className="text-indigo-600 mx-auto" strokeWidth={2.5} />;
  if (value === false) return <Minus size={15} className="text-slate-300 mx-auto" />;
  return <span className="text-[12px] text-slate-700 cf-num">{value}</span>;
};

const ComparisonTable = () => (
  <section className="relative py-24 bg-slate-50/60" data-testid="pricing-comparison">
    <div className="max-w-[1200px] mx-auto px-6">
      <Reveal>
        <div className="text-center max-w-[760px] mx-auto mb-12">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 text-[10.5px] font-semibold uppercase tracking-[0.22em]">
            Compare plans
          </span>
          <h2 className="font-display text-[36px] sm:text-[44px] font-bold tracking-[-0.03em] text-slate-900 leading-[1.05] mt-6">
            Every feature, side-by-side.
          </h2>
        </div>
      </Reveal>

      <Reveal>
        <div className="rounded-3xl border border-slate-200 bg-white overflow-hidden" style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 12px 28px -12px rgba(15,23,42,0.06)" }}>
          {/* Header row */}
          <div className="grid grid-cols-12 px-7 py-5 border-b border-slate-200 sticky top-[68px] z-10 bg-white/90 backdrop-blur-md">
            <div className="col-span-6"></div>
            <div className="col-span-2 text-center">
              <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Core</p>
            </div>
            <div className="col-span-2 text-center">
              <p className="text-[10px] uppercase tracking-[0.22em] text-indigo-700 font-bold inline-flex items-center gap-1">
                Pro <Sparkles size={10} />
              </p>
            </div>
            <div className="col-span-2 text-center">
              <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Enterprise</p>
            </div>
          </div>

          {sections.map((section) => (
            <div key={section.title} className="border-b border-slate-100 last:border-0">
              <div className="px-7 py-3.5 bg-slate-50/60">
                <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">{section.title}</p>
              </div>
              {section.rows.map((r, i) => (
                <div
                  key={r.l}
                  className={`grid grid-cols-12 px-7 py-3.5 items-center transition-colors hover:bg-slate-50 ${
                    i !== 0 ? "border-t border-slate-100" : ""
                  }`}
                  data-testid={`row-${r.l.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="col-span-6 text-[13px] text-slate-700">{r.l}</div>
                  <div className="col-span-2 text-center"><Cell value={r.core} /></div>
                  <div className="col-span-2 text-center bg-indigo-50/40">
                    <div className="inline-block px-1 py-1.5">
                      <Cell value={r.pro} />
                    </div>
                  </div>
                  <div className="col-span-2 text-center"><Cell value={r.ent} /></div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

/* ─── FAQ ─── */
const faqs = [
  {
    q: "How does per-branch pricing work?",
    a: "We charge per active branch, with unlimited users on Core and Pro within plan limits. Most agencies start with 1–2 branches and upgrade as they roll out across the group. Enterprise removes branch caps entirely.",
  },
  {
    q: "Is there a free trial?",
    a: "Yes — every plan includes a 14-day free trial with full access to the platform. No credit card required. Our team will run a guided onboarding so you're live on day one.",
  },
  {
    q: "Can we migrate from another platform?",
    a: "Absolutely. We've migrated agencies from Reapit, Alto and bespoke spreadsheet stacks. Pro and Enterprise include data import support; Enterprise includes a dedicated migration engineer.",
  },
  {
    q: "What's included in 'AI Risk Intelligence'?",
    a: "Predictive fall-through scoring, solicitor performance ranking, sentiment + behavioural signals, exchange forecasting and chain health mapping. The model runs continuously across every active transaction.",
  },
  {
    q: "Can solicitors and brokers be added at no extra cost?",
    a: "Yes. External parties (solicitors, brokers, surveyors, buyers, sellers) are always free. You only pay for your in-house users.",
  },
  {
    q: "Do you offer custom contracts and procurement support?",
    a: "Yes — Enterprise customers receive custom MSAs, security questionnaires, DPAs and dedicated procurement support. Annual contracts unlock significant discount tiers.",
  },
];

const FAQ = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="relative py-24" data-testid="pricing-faq">
      <div className="max-w-[920px] mx-auto px-6">
        <Reveal>
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 text-[10.5px] font-semibold uppercase tracking-[0.22em]">
              Frequently asked
            </span>
            <h2 className="font-display text-[36px] sm:text-[44px] font-bold tracking-[-0.03em] text-slate-900 leading-[1.05] mt-6">
              Pricing questions, answered.
            </h2>
          </div>
        </Reveal>
        <Reveal>
          <div className="rounded-2xl border border-slate-200 bg-white" style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 12px 28px -12px rgba(15,23,42,0.06)" }}>
            {faqs.map((f, i) => (
              <div key={i} className={`${i !== 0 ? "border-t border-slate-100" : ""}`}>
                <button
                  data-testid={`faq-${i}`}
                  onClick={() => setOpen(open === i ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 px-7 py-5 text-left transition-colors hover:bg-slate-50"
                >
                  <span className="text-[14.5px] font-semibold text-slate-900">{f.q}</span>
                  <span
                    className={`w-7 h-7 shrink-0 rounded-full border flex items-center justify-center transition-all duration-300 ${
                      open === i
                        ? "rotate-45 bg-indigo-100 border-indigo-300 text-indigo-700"
                        : "border-slate-200 text-slate-500"
                    }`}
                  >
                    +
                  </span>
                </button>
                <div
                  className="overflow-hidden transition-all duration-400 ease-out"
                  style={{ maxHeight: open === i ? 240 : 0 }}
                >
                  <p className="px-7 pb-6 text-[13.5px] text-slate-600 leading-relaxed max-w-[760px]">{f.a}</p>
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* ─── Final CTA ─── */
const FinalCTA = () => (
  <section id="contact" className="relative py-24" data-testid="pricing-final-cta">
    <div className="max-w-[1100px] mx-auto px-6">
      <Reveal>
        <div className="relative rounded-[28px] overflow-hidden p-12 sm:p-16 text-center border border-indigo-300/30"
          style={{
            background: "linear-gradient(140deg, #1E1B4B 0%, #0F0F2D 60%, #0A0A1F 100%)",
            boxShadow: "0 60px 140px -40px rgba(99, 102, 241,0.50)",
          }}
        >
          <div className="absolute -top-32 -left-32 w-80 h-80 cf-blob opacity-50" />
          <div className="absolute -bottom-40 -right-32 w-96 h-96 rounded-full bg-indigo-400/15 blur-3xl" />
          <div className="relative">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-indigo-200 text-[10.5px] font-semibold uppercase tracking-[0.22em] backdrop-blur-md">
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 cf-pulse" />
              Built for premium agencies
            </span>
            <h2 className="font-display text-[40px] sm:text-[56px] font-bold tracking-[-0.03em] text-white leading-[1.04] mt-7 max-w-[840px] mx-auto">
              The future of property
              <br />
              <span
                className="cf-grad-text"
                style={{ backgroundImage: "linear-gradient(135deg,#A5B4FC,#818CF8 50%,#6366F1)" }}
              >
                transactions starts here.
              </span>
            </h2>
            <p className="text-[16px] text-slate-300 mt-6 max-w-[600px] mx-auto leading-relaxed">
              See ChainFlow run on your own pipeline data. We'll build you a modelled ROI estimate based on your branch volume.
            </p>
            <div className="flex items-center justify-center gap-3 mt-9 flex-wrap">
              <a href="mailto:hello@chainflow.io" data-testid="pricing-final-book-demo"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-xl text-[14.5px] font-semibold text-white transition-all"
                style={{
                  background: "linear-gradient(180deg, #6366F1 0%, #4F6BFF 100%)",
                  boxShadow: "0 14px 36px -10px rgba(99, 102, 241, 0.55), inset 0 1px 0 rgba(255,255,255,0.22)",
                }}
              >Book demo <ArrowRight size={15} /></a>
              <a href="mailto:hello@chainflow.io" data-testid="pricing-final-contact"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-xl text-[14.5px] font-semibold text-white border border-white/15 hover:border-white/30 backdrop-blur-md bg-white/[0.04] hover:bg-white/[0.08] transition-all"
              >Speak with us <Mail size={14} /></a>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ─── Page Root ─── */
export default function PricingPage() {
  const [billing, setBilling] = useState("yearly");
  return (
    <div className="min-h-screen overflow-x-hidden bg-white text-slate-900" data-testid="pricing-page">
      <TopNav />
      <PricingHeader billing={billing} setBilling={setBilling} />
      <PricingTiers billing={billing} />
      <ValuePositioning />
      <ROISection />
      <ComparisonTable />
      <FAQ />
      <FinalCTA />
      <Footer />
    </div>
  );
}
